/**
 * GDPR/RGPD-compliant Consent Mode helpers for Google Analytics (GA4).
 * - Defaults are DENIED on first visit (set inline in index.html before gtag loads).
 * - Choices persisted in localStorage.
 * - Listeners notified through a custom event so any component can react.
 */
export const CONSENT_KEY = "fol_consent_v2";

export const DEFAULT_CONSENT = {
    analytics: false,
    // Functional (session cookie / auth) is always ON — required for the site to work.
};

export function readConsent() {
    try {
        const raw = localStorage.getItem(CONSENT_KEY);
        if (!raw) return null;
        const c = JSON.parse(raw);
        return { ...DEFAULT_CONSENT, ...c };
    } catch {
        return null;
    }
}

export function saveConsent(consent) {
    try {
        localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...consent, _ts: Date.now() }));
    } catch {}
    applyConsentToGtag(consent);
    // Strict RGPD/DSGVO §25 TTDSG: when analytics consent is withdrawn, actively
    // purge existing GA cookies. `gtag('consent', 'update', 'denied')` alone only
    // blocks FUTURE hits — it does not remove cookies already stored.
    if (!consent.analytics) {
        purgeAnalyticsCookies();
    }
    // Broadcast so the banner / footer button can react
    window.dispatchEvent(new CustomEvent("fol:consent-changed", { detail: consent }));
}

/**
 * Deletes Google Analytics cookies (_ga, _gid, _gat, _ga_XXXX) across every
 * plausible path/domain scope so no stale identifier survives a consent withdrawal.
 */
export function purgeAnalyticsCookies() {
    if (typeof document === "undefined") return;
    const host = window.location.hostname;
    // Build the list of domain scopes GA might have used
    const domains = new Set(["", host, `.${host}`]);
    // Also try the eTLD+1 (e.g. `.foundation-of-light.com` when we're on `www.foundation-of-light.com`)
    const parts = host.split(".");
    if (parts.length > 2) {
        const eTldPlus1 = parts.slice(-2).join(".");
        domains.add(eTldPlus1);
        domains.add(`.${eTldPlus1}`);
    }
    const kill = (name) => {
        domains.forEach((d) => {
            const domainAttr = d ? `; domain=${d}` : "";
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainAttr}`;
        });
    };
    document.cookie.split(";").forEach((raw) => {
        const name = raw.split("=")[0].trim();
        if (!name) return;
        if (name === "_ga" || name === "_gid" || name === "_gat" || name.startsWith("_ga_") || name.startsWith("_gat_")) {
            kill(name);
        }
    });
}

export function applyConsentToGtag(consent) {
    if (typeof window === "undefined") return;
    if (typeof window.gtag !== "function") return;
    window.gtag("consent", "update", {
        analytics_storage: consent.analytics ? "granted" : "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
    });
}

export function openConsentPreferences() {
    window.dispatchEvent(new CustomEvent("fol:open-consent"));
}
